import { Form, Formik, useFormikContext } from 'formik';
import { array, boolean, number, object, string } from 'yup';

import { Button } from '@design-system/button';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@design-system/dialog';
import { useDialogContext } from '@design-system/dialog/use-dialog-context';
import { Label } from '@design-system/label';
import MultipleSelector from '@design-system/multi-select';
import { Switch } from '@design-system/switch';
import { Textfield } from '@shared/fields/text-field';
import { TextAreafield } from '@shared/fields/text-area-field';
import { useAgents } from '@features/ai-agents/services/agents-api';

import {
  knowledgeBaseApi,
  type KnowledgeBase,
  type KnowledgeBasePayload,
} from '../services/knowledge-base-api';

export type KbaseFormValues = {
  name: string;
  description: string;
  is_active: boolean;
  ai_agent_ids: number[];
};

export const kbaseFormSchema = object({
  name: string()
    .trim()
    .required('Name is required')
    .max(255, 'At most 255 characters'),
  description: string().max(2000, 'At most 2000 characters'),
  is_active: boolean(),
  ai_agent_ids: array().of(number().integer()),
});

/** Shared with Tia’s inline create form so both surfaces edit the same fields. */
export function KbaseAgentsField() {
  const { values, setFieldValue } = useFormikContext<KbaseFormValues>();
  const { data: agents = [], isPending } = useAgents();
  const options = agents.map((agent) => ({
    value: String(agent.id),
    label: agent.name,
  }));
  const selected = options.filter((option) =>
    values.ai_agent_ids.includes(Number(option.value))
  );

  return (
    <div>
      <Label htmlFor='ai_agent_ids'>Agents</Label>
      <MultipleSelector
        value={selected}
        options={options}
        placeholder={
          isPending
            ? 'Loading agents…'
            : 'Pick the agents that answer from this base'
        }
        emptyIndicator={
          <p className='p-2 text-center text-sm text-gray-500'>
            No more agents to add.
          </p>
        }
        hidePlaceholderWhenSelected
        commandProps={{ label: 'Agents', id: 'ai_agent_ids' }}
        onChange={(picked) =>
          setFieldValue(
            'ai_agent_ids',
            picked.map((option) => Number(option.value))
          )
        }
      />
      <p className='mt-1.5 text-xs text-gray-500'>
        An agent answers only from the bases attached to it.
      </p>
    </div>
  );
}

/** Shared with Tia’s inline create form so both surfaces edit the same fields. */
export function KbaseActiveField() {
  const { values, setFieldValue } = useFormikContext<KbaseFormValues>();
  return (
    <label className='flex items-center justify-between rounded-lg border border-pale-border bg-primary-30 px-3 py-2.5'>
      <span className='text-sm text-gray-800'>Active</span>
      <Switch
        checked={values.is_active}
        onCheckedChange={(checked) => setFieldValue('is_active', checked)}
      />
    </label>
  );
}

function KbaseForm({
  kbase,
  defaultAgentIds,
  onDone,
}: {
  kbase: KnowledgeBase | null;
  defaultAgentIds?: number[];
  onDone: (saved: KnowledgeBase) => void;
}) {
  const { formikRef } = useDialogContext();
  const create = knowledgeBaseApi.useCreate();
  const update = knowledgeBaseApi.useUpdate();
  const isPending = create.isPending || update.isPending;

  return (
    <Formik<KbaseFormValues>
      innerRef={formikRef}
      enableReinitialize
      validationSchema={kbaseFormSchema}
      initialValues={{
        name: kbase?.name ?? '',
        description: kbase?.description ?? '',
        is_active: kbase?.is_active ?? true,
        ai_agent_ids:
          kbase?.ai_agents.map((agent) => agent.id) ?? defaultAgentIds ?? [],
      }}
      onSubmit={async (values) => {
        const payload: KnowledgeBasePayload = {
          name: values.name.trim(),
          description: values.description.trim() || null,
          is_active: values.is_active,
          ai_agent_ids: values.ai_agent_ids,
        };
        const saved = kbase
          ? await update.mutateAsync({ id: kbase.id, ...payload })
          : await create.mutateAsync(payload);
        onDone(saved);
      }}
    >
      {({ submitForm }) => (
        <Form className='flex min-h-0 flex-1 flex-col'>
          <DialogBody className='flex flex-col gap-4'>
            <Textfield
              name='name'
              label='Name'
              placeholder='Enter Knowledge Base Name'
            />
            <TextAreafield
              name='description'
              label='Description'
              placeholder='What this base covers...'
              withMaxLength={false}
              labelProps={{
                className: 'mb-0',
              }}
            />
            <KbaseAgentsField />
            <KbaseActiveField />
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button
                type='button'
                variant='primaryOutline'
                className='flex-1 border-primary-200 text-primary-1000'
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type='button'
              variant='primary'
              className='flex-1'
              isLoading={isPending}
              onClick={submitForm}
            >
              {kbase ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </Form>
      )}
    </Formik>
  );
}

export function KbaseFormDialog({
  kbase,
  open,
  onOpenChange,
  onSaved,
  defaultAgentIds,
}: {
  /** null creates a new base. */
  kbase: KnowledgeBase | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (saved: KnowledgeBase) => void;
  defaultAgentIds?: number[];
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        align='right'
        className='max-w-[600px] rounded-2xl'
      >
        <DialogHeader>
          <DialogTitle>
            {kbase ? 'Edit knowledge base' : 'New knowledge base'}
          </DialogTitle>
        </DialogHeader>
        <KbaseForm
          kbase={kbase}
          defaultAgentIds={defaultAgentIds}
          onDone={(saved) => {
            onOpenChange(false);
            onSaved?.(saved);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
